import { useEffect } from "react";
import LakeCharlesHeroSection from "./components/HeroSection";
import LakeCharlesStickyNav from "./components/StickyNav";
import LakeCharlesProcessingTimeSection from "./components/ProcessingTimeSection";
import LakeCharlesServicesSection from "./components/ServicesSection";
import LakeCharlesLocalAreaSection from "./components/LocalAreaSection";
import LakeCharlesTravelTimingSection from "./components/TravelTimingSection";
import LakeCharlesDestinationsSection from "./components/DestinationsSection";
import LakeCharlesTestimonialsSection from "./components/TestimonialsSection";
import LakeCharlesAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import LakeCharlesComparisonSection from "./components/ComparisonSection";
import LakeCharlesMistakesSection from "./components/MistakesSection";
import LakeCharlesHowItWorksSection from "./components/HowItWorksSection";
import LakeCharlesFAQSection, { faqs } from "./components/FAQSection";
import LakeCharlesHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import LakeCharlesFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function LakeCharlesPage() {
  useEffect(() => {
    document.title = "Emergency Passport Lake Charles, LA — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/lake-charles/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Lake Charles, LA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving petrochemical engineers, casino professionals, McNeese students, and Southwest Louisiana families.");
    setMeta("geo.placename", "Lake Charles, Louisiana");
    setMeta("geo.region", "US-LA");
    setMeta("geo.position", "30.2266;-93.2174");
    setMeta("ICBM", "30.2266, -93.2174");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Louisiana", item: "https://fastpassportcenter.com/louisiana/" },
          { "@type": "ListItem", position: 3, name: "Lake Charles Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Lake Charles, LA — as fast as 24 hours. Registered U.S. Department of State courier serving Southwest Louisiana.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Lake Charles", containedInPlace: { "@type": "State", name: "Louisiana" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-lakecharles";
    document.getElementById("structured-data-lakecharles")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data-lakecharles")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <LakeCharlesHeroSection />
      <LakeCharlesStickyNav />
      <LakeCharlesProcessingTimeSection />
      <LakeCharlesServicesSection />
      <LakeCharlesLocalAreaSection />
      <LakeCharlesTravelTimingSection />
      <LakeCharlesDestinationsSection />
      <LakeCharlesTestimonialsSection />
      <LakeCharlesAcceptanceFacilitiesSection />
      <LakeCharlesComparisonSection />
      <LakeCharlesMistakesSection />
      <LakeCharlesHowItWorksSection />
      <LakeCharlesFAQSection />
      <LakeCharlesHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/lake-charles" state="LA" />
      <LakeCharlesFinalCTA />
    </div>
  );
}